import ApiService from './api.service'
import type {
  TaskResponse,
  TaskListResponse,
  TaskMinimalListResponse,
  TaskBulkResponse,
  TaskData,
  TaskSearchOptions,
  TaskBulkUpdateData
} from '@/types/services'
import type {
  TaskStatusHistoryDto,
  TaskStatusChangeRequest,
  TaskDto
} from '@/types/task'

/**
 * Service for managing project task-related API endpoints
 */
class ProjectTasksService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Tasks Error'
      }
    })
  }

  /**
   * Fetch tasks for a specific project
   */
  async getTasks(projectId: string): Promise<TaskListResponse | null> {
    return this.get(`/api/project/${projectId}/tasks`)
  }

  /**
   * Get a specific task by ID
   */
  async getTaskById(projectId: string, taskId: string): Promise<TaskResponse | null> {
    return this.get(`/api/project/${projectId}/tasks/${taskId}`)
  }

  /**
   * Create a new task
   */
  async createTask(projectId: string, taskData: TaskData): Promise<TaskResponse | null> {
    return this.post(`/api/project/${projectId}/tasks`, taskData)
  }

  /**
   * Update an existing task
   */
  async updateTask(projectId: string, taskId: string, taskData: TaskData): Promise<TaskResponse | null> {
    return this.put(`/api/project/${projectId}/tasks/${taskId}`, taskData)
  }

  /**
   * Delete a task
   */
  async deleteTask(projectId: string, taskId: string): Promise<TaskResponse | null> {
    return this.delete(`/api/project/${projectId}/tasks/${taskId}`)
  }

  /**
   * Update task status
   */
  async updateTaskStatus(projectId: string, taskId: string, status: string): Promise<TaskResponse | null> {
    return this.put(`/api/project/${projectId}/tasks/${taskId}/status`, { status })
  }

  /**
   * Assign task to a user
   */
  async assignTask(projectId: string, taskId: string, assigneeEmail: string): Promise<TaskResponse | null> {
    return this.put(`/api/project/${projectId}/tasks/${taskId}/assign`, { assigneeEmail })
  }

  /**
   * Unassign task from current assignee
   */
  async unassignTask(projectId: string, taskId: string): Promise<TaskResponse | null> {
    return this.put(`/api/project/${projectId}/tasks/${taskId}/unassign`, {})
  }

  
  /**
   * Get upcoming tasks for a project
   */
  async getUpcomingTasks(
    projectId: string, 
    days: number = 7, 
    options: TaskSearchOptions = {}
  ): Promise<TaskListResponse | null> {
    const { page = 0, size = 10 } = options
    return this.get(`/api/project/${projectId}/tasks/upcoming`, {
      days,
      page,
      size
    })
  }

  /**
   * Duplicate a task
   */
  async duplicateTask(projectId: string, taskId: string): Promise<TaskResponse | null> {
    return this.post(`/api/project/${projectId}/tasks/${taskId}/duplicate`, {})
  }

  /**
   * Bulk update tasks
   */
  async bulkUpdateTasks(
    projectId: string, 
    taskIds: string[], 
    updateData: TaskBulkUpdateData
  ): Promise<TaskBulkResponse | null> {
    return this.put(`/api/project/${projectId}/tasks/bulk`, {
      taskIds,
      ...updateData
    })
  }

  /**
   * Bulk delete tasks
   */
  async bulkDeleteTasks(projectId: string, taskIds: string[]): Promise<TaskBulkResponse | null> {
    return this.delete(`/api/project/${projectId}/tasks/bulk`, {
      data: { taskIds }
    })
  }

  /**
   * Get status timeline for a task (chronological order)
   * Returns the complete status history for task status visualization
   * @param projectId - The project ID
   * @param taskId - The task ID
   * @returns Promise that resolves with list of status history entries
   */
  async getTaskStatusTimeline(projectId: string, taskId: string): Promise<TaskStatusHistoryDto[] | null> {
    return this.get<TaskStatusHistoryDto[]>(`/api/project/${projectId}/tasks/${taskId}/status-timeline`)
  }

  /**
   * Change task status using state machine
   * @param projectId - The project ID
   * @param taskId - The task ID
   * @param newStatus - The new status to transition to
   * @param comment - Optional comment about the status change
   * @returns Promise that resolves with updated task
   */
  async changeTaskStatus(
    projectId: string,
    taskId: string,
    newStatus: string,
    comment?: string
  ): Promise<TaskDto | null> {
    return this.post<TaskDto>(`/api/project/${projectId}/tasks/${taskId}/change-status`, {
      newStatus,
      comment
    })
  }

  /**
   * Get minimal task list (id and name only)
   */
  async getMinimalTasksList(projectId: string): Promise<TaskMinimalListResponse | null> {
    return this.get(`/api/project/${projectId}/tasks/minimal-list`)
  }
}

// Create and export a singleton instance
export default new ProjectTasksService()
